/**
 * ManagerFormSheet
 * Sheet deslizable para crear o editar un gerente (role_id = 3).
 *  - Modo crear  → manager prop omitido / null
 *  - Modo editar → manager prop con el gerente existente
 *
 * A diferencia del dueño, el gerente se asigna a una empresa específica
 * Y a una sucursal específica de esa empresa.
 *
 * En modo editar incluye una zona de estado para dar de baja o reactivar.
 * El estilo sigue el mismo patrón que OwnerFormSheet / BranchFormSheet.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  UserCog,
  Save,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  Package,
  DollarSign,
  BarChart3,
  Boxes,
  Trash2,
  Link2,
  Copy,
  Check,
  Users,
  Tag,
} from "lucide-react";
import { usersService, type User } from "@/services/usersService";
import { createInvitation } from "@/services/invitationsService";
import { copyToClipboard } from "@/lib/clipboard";
import { type Company } from "@/services/companiesService";
import { type Branch } from "@/services/branchesService";
import UserAvatar, { avatarGradients } from "@/components/UserAvatar";
import { verifyCredentials, getCurrentUser } from "@/services/authService";
import DeleteWithAuthDialog from "@/components/DeleteWithAuthDialog";

// ─── modules_config types & helpers ──────────────────────────────────────────

/** CRUD sub-permisos (products / stock). */
type CrudFlags = { view: boolean; create: boolean; edit: boolean; delete: boolean };

interface ModulesConfig {
  /** Prefer nested CRUD; boolean still accepted when reading legacy configs. */
  stock: CrudFlags;
  prices: boolean;
  products: CrudFlags;
  customers: boolean;
  discounts: boolean;
  "financial-summary": { view: boolean; ventas: boolean; turnos: boolean };
  [key: string]: unknown;
}

const CRUD_OFF: CrudFlags = { view: false, create: false, edit: false, delete: false };
const CRUD_ON: CrudFlags = { view: true, create: true, edit: true, delete: true };

/**
 * Normaliza `stock` / `products`:
 * - `true`  → full CRUD (legacy boolean)
 * - `false` / missing → all off
 * - object  → merge flags; legacy `adjust: true` maps to create+edit+delete
 */
function normalizeCrud(raw: unknown, fallback: CrudFlags = CRUD_OFF): CrudFlags {
  if (raw === true) return { ...CRUD_ON };
  if (raw === false || raw == null) return { ...fallback };
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const adjust = o.adjust === true;
    return {
      view: o.view === true || adjust,
      create: o.create === true || adjust,
      edit: o.edit === true || adjust,
      delete: o.delete === true || adjust,
    };
  }
  return { ...fallback };
}

const DEFAULT_MODULES: ModulesConfig = {
  stock: { ...CRUD_OFF },
  prices: false,
  products: { ...CRUD_OFF },
  customers: false,
  discounts: false,
  "financial-summary": { view: false, ventas: false, turnos: false },
};

function normalizeModules(raw?: Record<string, unknown>): ModulesConfig {
  const r = (raw ?? {}) as Record<string, any>;
  return {
    stock: normalizeCrud(r.stock, CRUD_OFF),
    prices: r.prices ?? false,
    products: (() => {
      const p = normalizeCrud(r.products, CRUD_OFF);
      // Product delete is not offered in the admin panel
      return { ...p, delete: false };
    })(),
    customers: r.customers ?? false,
    discounts: r.discounts ?? false,
    "financial-summary": {
      view: r["financial-summary"]?.view ?? false,
      ventas: r["financial-summary"]?.ventas ?? false,
      turnos: r["financial-summary"]?.turnos ?? false,
    },
  };
}

function crudAnyOn(c: CrudFlags): boolean {
  return c.view || c.create || c.edit || c.delete;
}

// ─── small UI helpers ─────────────────────────────────────────────────────────

function ModuleRow({
  label,
  description,
  icon,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  description?: string;
  icon: React.ReactNode;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="text-white/40 flex-shrink-0">{icon}</div>
        <div className="min-w-0">
          <p className="text-sm text-white leading-tight">{label}</p>
          {description && (
            <p className="text-xs text-white/30 leading-tight mt-0.5">{description}</p>
          )}
        </div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="flex-shrink-0 data-[state=checked]:bg-blue-600"
      />
    </div>
  );
}

function SubRow({
  label,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-white/60">{label}</p>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="flex-shrink-0 data-[state=checked]:bg-blue-500"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface ManagerFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Gerente a editar. Omitir o pasar null para modo creación. */
  manager?: User | null;
  /** Lista de empresas disponibles para el selector. */
  allCompanies: Company[];
  /** Lista de todas las sucursales para filtrar por empresa seleccionada. */
  allBranches: Branch[];
  onSave: (manager?: User) => void;
}

export default function ManagerFormSheet({
  isOpen,
  onClose,
  manager,
  allCompanies,
  allBranches,
  onSave,
}: ManagerFormSheetProps) {
  const isEditing = !!manager;

  const [companyId, setCompanyId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [modulesConfig, setModulesConfig] = useState<ModulesConfig>(DEFAULT_MODULES);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null);
  const [invitationGenerated, setInvitationGenerated] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Sucursales activas filtradas por la empresa seleccionada
  const availableBranches = allBranches.filter(
    (b) => b.company_id === companyId && b.is_active,
  );

  // Sync campos cuando cambia el gerente o se abre el panel
  useEffect(() => {
    if (isOpen) {
      setCompanyId(manager?.company_id ?? "");
      setBranchId(manager?.branch_id ?? "");
      setFirstName(manager?.first_name ?? "");
      setLastName(manager?.last_name ?? "");
      setUsername(manager?.username ?? "");
      setEmail(manager?.email ?? "");
      setPassword("");
      setModulesConfig(normalizeModules(manager?.modules_config as Record<string, unknown> | undefined));
      setInvitationUrl(null);
      setInvitationGenerated(false);
      setIsCopied(false);
    }
  }, [isOpen, manager]);

  // Al cambiar empresa, limpiar sucursal si ya no pertenece a ella
  useEffect(() => {
    if (branchId) {
      const stillValid = allBranches.some(
        (b) => b.id === branchId && b.company_id === companyId,
      );
      if (!stillValid) setBranchId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleClose = () => {
    if (isSaving || isToggling) return;
    onClose();
  };

  const handleSave = async () => {
    if (!isEditing) {
      // Modo creación: generar invitación
      if (!companyId) {
        toast.error("Debes seleccionar una empresa");
        return;
      }
      if (!branchId) {
        toast.error("Debes seleccionar una sucursal");
        return;
      }
      setIsSaving(true);
      try {
        const inv = await createInvitation({
          company_id: companyId,
          branch_id: branchId,
          role_id: 3,
          modules_config: modulesConfig,
        });
        setInvitationUrl(inv.invitation_url);
        setInvitationGenerated(true);
        toast.success("Invitación generada correctamente");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Error al generar la invitación",
        );
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Modo edición: validaciones completas
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Nombre y apellido son obligatorios");
      return;
    }
    if (!companyId) {
      toast.error("Debes seleccionar una empresa");
      return;
    }
    if (!branchId) {
      toast.error("Debes seleccionar una sucursal");
      return;
    }

    setIsSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: any = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || null,
        company_id: companyId,
        branch_id: branchId,
        modules_config: modulesConfig,
      };
      if (password) updateData.password = password;
      const saved = await usersService.updateUser(manager.id, updateData);
      toast.success("Gerente actualizado correctamente");
      onSave(saved);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al guardar el gerente",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!manager) return;
    setIsToggling(true);
    try {
      let updated: User;
      if (manager.is_active) {
        updated = await usersService.deactivateUser(manager.id);
        toast.success("Gerente dado de baja correctamente");
      } else {
        updated = await usersService.activateUser(manager.id);
        toast.success("Gerente reactivado correctamente");
      }
      onSave(updated);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al cambiar el estado",
      );
    } finally {
      setIsToggling(false);
    }
  };

  const handleDeleteConfirm = async (password: string) => {
    if (!manager) return;
    const currentUser = getCurrentUser();
    if (!currentUser) { toast.error("Sesión no encontrada"); return; }
    setIsDeleting(true);
    try {
      const valid = await verifyCredentials(currentUser.username, password);
      if (!valid) { toast.error("Contraseña incorrecta"); return; }
      await usersService.deleteUser(manager.id);
      toast.success("Gerente eliminado correctamente");
      setIsDeleteOpen(false);
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al eliminar el gerente");
    } finally {
      setIsDeleting(false);
    }
  };

  const initials = isEditing
    ? usersService.getInitials(manager)
    : (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || "?";

  return (
    <>
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            {isEditing ? (
              <UserAvatar initials={initials} role="manager" size="xl" />
            ) : (
              <div className={`w-14 h-14 rounded-2xl ${avatarGradients.manager} flex items-center justify-center text-white flex-shrink-0`}>
                <UserCog className="h-7 w-7" />
              </div>
            )}
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {isEditing ? "Editar gerente" : "Nuevo gerente"}
              </SheetTitle>
              {isEditing && (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-sm text-white/50">@{manager.username}</p>
                  <Badge
                    className={
                      manager.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30"
                    }
                  >
                    {manager.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Cuerpo con scroll */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Datos de acceso + personales — solo en edición */}
            {isEditing && (<>
            <section className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Datos de acceso
              </p>

              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="manager-username" className="text-white/70 text-sm">
                  Usuario <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="manager-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="jperez"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40 disabled:opacity-50"
                  disabled={isEditing || isSaving || isToggling}
                  autoFocus={!isEditing}
                  autoComplete="off"
                />
                {isEditing && (
                  <p className="text-xs text-white/30">
                    El nombre de usuario no se puede modificar.
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="manager-password" className="text-white/70 text-sm">
                  Contraseña{" "}
                  {isEditing ? (
                    <span className="text-white/30 text-xs">(dejar vacío para no cambiar)</span>
                  ) : (
                    <span className="text-red-400">*</span>
                  )}
                </Label>
                <Input
                  id="manager-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isEditing ? "••••••••" : "Mínimo 8 caracteres"}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40"
                  disabled={isSaving || isToggling}
                  autoComplete="new-password"
                />
              </div>
            </section>

            <Separator className="bg-white/10" />

            {/* Datos personales */}
            <section className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Datos personales
              </p>

              {/* Nombre */}
              <div className="space-y-2">
                <Label htmlFor="manager-firstname" className="text-white/70 text-sm">
                  Nombre <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="manager-firstname"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Juan"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40"
                  disabled={isSaving || isToggling}
                  autoFocus={isEditing}
                />
              </div>

              {/* Apellido */}
              <div className="space-y-2">
                <Label htmlFor="manager-lastname" className="text-white/70 text-sm">
                  Apellido <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="manager-lastname"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Pérez"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40"
                  disabled={isSaving || isToggling}
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="manager-email" className="text-white/70 text-sm">
                  Correo electrónico{" "}
                  <span className="text-white/30 text-xs">(opcional)</span>
                </Label>
                <Input
                  id="manager-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="juan@empresa.com"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40"
                  disabled={isSaving || isToggling}
                />
              </div>
            </section>

            <Separator className="bg-white/10" />
            </>)}

            {/* Asignación */}
            <section className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Asignación
              </p>

              {/* Empresa */}
              <div className="space-y-2">
                <Label htmlFor="manager-company" className="text-white/70 text-sm">
                  Empresa <span className="text-red-400">*</span>
                </Label>
                <Select
                  value={companyId}
                  onValueChange={setCompanyId}
                  disabled={isSaving || isToggling}
                >
                  <SelectTrigger
                    id="manager-company"
                    className="bg-white/5 border-white/20 text-white focus:ring-0 focus:border-white/40"
                  >
                    <SelectValue placeholder="Seleccionar empresa..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/10 text-white">
                    {allCompanies
                      .filter((c) => c.is_active)
                      .map((c) => (
                        <SelectItem
                          key={c.id}
                          value={c.id}
                          className="focus:bg-white/10 focus:text-white"
                        >
                          {c.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sucursal — filtrada por empresa seleccionada */}
              <div className="space-y-2">
                <Label htmlFor="manager-branch" className="text-white/70 text-sm">
                  Sucursal <span className="text-red-400">*</span>
                </Label>
                <Select
                  value={branchId}
                  onValueChange={setBranchId}
                  disabled={!companyId || isSaving || isToggling}
                >
                  <SelectTrigger
                    id="manager-branch"
                    className="bg-white/5 border-white/20 text-white focus:ring-0 focus:border-white/40 disabled:opacity-50"
                  >
                    <SelectValue
                      placeholder={
                        companyId
                          ? "Seleccionar sucursal..."
                          : "Primero selecciona una empresa"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/10 text-white">
                    {availableBranches.length === 0 ? (
                      <div className="py-3 px-2 text-sm text-white/40 text-center">
                        Sin sucursales activas
                      </div>
                    ) : (
                      availableBranches.map((b) => (
                        <SelectItem
                          key={b.id}
                          value={b.id}
                          className="focus:bg-white/10 focus:text-white"
                        >
                          {b.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </section>

            {/* Permisos del panel */}
            <>
              <Separator className="bg-white/10" />
              <section className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                  <ShieldCheck className="inline-block h-3 w-3 mr-1 mb-0.5" />
                  Acceso al panel de administración
                </p>

                {/* Inventario / Stock */}
                <div className="space-y-2">
                  <ModuleRow
                    label="Inventario / Stock"
                    description="Ver y ajustar niveles de inventario"
                    icon={<Boxes className="h-4 w-4" />}
                    checked={crudAnyOn(modulesConfig.stock)}
                    onCheckedChange={(v) =>
                      setModulesConfig((p) => ({
                        ...p,
                        stock: { view: v, create: v, edit: v, delete: false },
                      }))
                    }
                    disabled={isSaving || isToggling}
                  />
                  <div className="ml-7 pl-4 border-l border-white/10 space-y-2">
                    <SubRow
                      label="Ver listado"
                      checked={modulesConfig.stock.view}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          stock: { ...p.stock, view: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Agregar"
                      checked={modulesConfig.stock.create}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          stock: { ...p.stock, create: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Editar"
                      checked={modulesConfig.stock.edit}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          stock: { ...p.stock, edit: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Reducir"
                      checked={modulesConfig.stock.delete}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          stock: { ...p.stock, delete: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                  </div>
                </div>

                {/* Precios */}
                <ModuleRow
                  label="Precios"
                  description="Ver y gestionar precios de productos"
                  icon={<DollarSign className="h-4 w-4" />}
                  checked={modulesConfig.prices}
                  onCheckedChange={(v) =>
                    setModulesConfig((p) => ({ ...p, prices: v }))
                  }
                  disabled={isSaving || isToggling}
                />

                {/* Productos */}
                <div className="space-y-2">
                  <ModuleRow
                    label="Productos"
                    description="Módulo de gestión de productos"
                    icon={<Package className="h-4 w-4" />}
                    checked={crudAnyOn(modulesConfig.products)}
                    onCheckedChange={(v) =>
                      setModulesConfig((p) => ({
                        ...p,
                        products: { view: v, create: v, edit: v, delete: false },
                      }))
                    }
                    disabled={isSaving || isToggling}
                  />
                  <div className="ml-7 pl-4 border-l border-white/10 space-y-2">
                    <SubRow
                      label="Ver listado"
                      checked={modulesConfig.products.view}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          products: { ...p.products, view: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Crear"
                      checked={modulesConfig.products.create}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          products: { ...p.products, create: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Editar"
                      checked={modulesConfig.products.edit}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          products: { ...p.products, edit: v },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                  </div>
                </div>

                {/* Clientes */}
                <ModuleRow
                  label="Clientes"
                  description="Gestión de clientes y cuentas corrientes"
                  icon={<Users className="h-4 w-4" />}
                  checked={modulesConfig.customers}
                  onCheckedChange={(v) =>
                    setModulesConfig((p) => ({ ...p, customers: v }))
                  }
                  disabled={isSaving || isToggling}
                />

                {/* Descuentos */}
                <ModuleRow
                  label="Descuentos"
                  description="Configuración y aplicación de descuentos"
                  icon={<Tag className="h-4 w-4" />}
                  checked={modulesConfig.discounts}
                  onCheckedChange={(v) =>
                    setModulesConfig((p) => ({ ...p, discounts: v }))
                  }
                  disabled={isSaving || isToggling}
                />

                {/* Resumen financiero */}
                <div className="space-y-2">
                  <ModuleRow
                    label="Resumen financiero"
                    description="Acceso al módulo de reportes financieros"
                    icon={<BarChart3 className="h-4 w-4" />}
                    checked={
                      modulesConfig["financial-summary"].view ||
                      modulesConfig["financial-summary"].ventas ||
                      modulesConfig["financial-summary"].turnos
                    }
                    onCheckedChange={(v) =>
                      setModulesConfig((p) => ({
                        ...p,
                        "financial-summary": { view: v, ventas: v, turnos: v },
                      }))
                    }
                    disabled={isSaving || isToggling}
                  />
                  <div className="ml-7 pl-4 border-l border-white/10 space-y-2">
                    <SubRow
                      label="Ver resumen general"
                      checked={modulesConfig["financial-summary"].view}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          "financial-summary": {
                            ...p["financial-summary"],
                            view: v,
                          },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Ventas"
                      checked={modulesConfig["financial-summary"].ventas}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          "financial-summary": {
                            ...p["financial-summary"],
                            ventas: v,
                          },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                    <SubRow
                      label="Turnos"
                      checked={modulesConfig["financial-summary"].turnos}
                      onCheckedChange={(v) =>
                        setModulesConfig((p) => ({
                          ...p,
                          "financial-summary": {
                            ...p["financial-summary"],
                            turnos: v,
                          },
                        }))
                      }
                      disabled={isSaving || isToggling}
                    />
                  </div>
                </div>
              </section>
            </>

            {/* Invitación generada */}
            {!isEditing && invitationGenerated && (
              <>
                <Separator className="bg-white/10" />
                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400/80">
                    <Link2 className="inline-block h-3 w-3 mr-1 mb-0.5" />
                    Invitación lista
                  </p>
                  <p className="text-xs text-white/50">
                    Comparte este enlace con el futuro gerente. Expira en 7 días.
                  </p>
                  <div className="flex items-center gap-2 bg-white/5 rounded-lg border border-white/10 px-3 py-2">
                    <p className="text-xs text-blue-300 break-all flex-1 select-all">
                      {invitationUrl}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        copyToClipboard(invitationUrl ?? "");
                        setIsCopied(true);
                        setTimeout(() => setIsCopied(false), 2000);
                      }}
                      className="flex-shrink-0 p-1.5 rounded-md hover:bg-white/10 transition-colors"
                      title="Copiar enlace"
                    >
                      {isCopied
                        ? <Check className="h-4 w-4 text-emerald-400" />
                        : <Copy className="h-4 w-4 text-white/50" />}
                    </button>
                  </div>
                </section>
              </>
            )}

            {/* Zona de estado — solo en edición */}
            {isEditing && (
              <>
                <Separator className="bg-white/10" />
                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Estado de la cuenta
                  </p>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      {manager.is_active ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white">
                          {manager.is_active ? "Cuenta activa" : "Cuenta inactiva"}
                        </p>
                        <p className="text-xs text-white/40">
                          {manager.is_active
                            ? "El gerente puede iniciar sesión normalmente."
                            : "El gerente no puede iniciar sesión."}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToggleActive}
                      disabled={isSaving || isToggling}
                      className={
                        manager.is_active
                          ? "w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent"
                          : "w-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 bg-transparent"
                      }
                    >
                      {isToggling ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : manager.is_active ? (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      {manager.is_active ? "Dar de baja" : "Reactivar"}
                    </Button>
                  </div>
                </section>

                <Separator className="bg-white/10" />

                {/* Zona de peligro */}
                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-red-400/70">
                    Zona de peligro
                  </p>
                  <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 space-y-3">
                    <p className="text-xs text-white/40">
                      Elimina al gerente permanentemente del sistema. Si solo quieres
                      bloquear el acceso, usa &ldquo;Dar de baja&rdquo; más arriba.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsDeleteOpen(true)}
                      disabled={isSaving || isToggling || isDeleting}
                      className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar gerente permanentemente
                    </Button>
                  </div>
                </section>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer fijo */}
        <SheetFooter
          className="flex-shrink-0 flex flex-row justify-center sm:justify-end gap-2 px-6 py-4
                     border-t border-white/10 bg-slate-900"
        >
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving || isToggling}
            className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          {isEditing ? (
            <Button
              onClick={handleSave}
              disabled={isSaving || isToggling}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Guardar cambios
            </Button>
          ) : invitationGenerated ? (
            <Button
              onClick={() => {
                setInvitationGenerated(false);
                setInvitationUrl(null);
                setCompanyId("");
                setBranchId("");
                setModulesConfig(DEFAULT_MODULES);
              }}
              variant="outline"
              className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
            >
              <Link2 className="h-4 w-4 mr-2" />
              Nueva invitación
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Generar invitación
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>

    {isEditing && manager && (
      <DeleteWithAuthDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        resourceType="Gerente"
        resourceName={usersService.getFullName(manager)}
        cascadeItems={[]}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    )}
    </>
  );
}
